import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_URL = '/api/masters/icds/';

const getAuthHeaders = (isFormData = false) => {
    const headers = { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

// Helper to determine text color based on background
const getTextColorForBackground = (hexColor) => {
    if (!hexColor) return '#000000';
    const rgb = parseInt(hexColor.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma < 128 ? '#FFFFFF' : '#000000';
};


// A separate component for the sortable item, as is standard with @dnd-kit
function SortableIcdItem({ id, item, isEditing, onEdit, onSave, onCancel, onDelete, hasPermission }) { // 2. Receive hasPermission
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id, disabled: isEditing }); // Disable dragging when editing

    const [editedName, setEditedName] = useState(item.name);
    const [editedShortName, setEditedShortName] = useState(item.short_name || '');
    const [editedCode, setEditedCode] = useState(item.code || '');
    const [editedColor, setEditedColor] = useState(item.color || '#FFFFFF');
    const [editedFreeDays, setEditedFreeDays] = useState(item.free_days ?? '');
    const [editedTerminal, setEditedTerminal] = useState(!!item.terminal);

    useEffect(() => {
        if (isEditing) {
            setEditedName(item.name);
            setEditedShortName(item.short_name || '');
            setEditedCode(item.code || '');
            setEditedColor(item.color || '#FFFFFF');
            setEditedFreeDays(item.free_days ?? '');
            setEditedTerminal(!!item.terminal);
        }
    }, [isEditing, item]);

    const handleSave = () => {
        if (!editedName.trim()) {
            toast.warn('ICD name cannot be empty.');
            return;
        }
        onSave(id, {
            name: editedName.trim().toUpperCase(),
            short_name: editedShortName.trim().toUpperCase(),
            code: editedCode.trim().toUpperCase(),
            color: editedColor,
            free_days: editedFreeDays === '' ? null : parseInt(editedFreeDays, 10),
            terminal: editedTerminal,
        });
    };

    const itemBackgroundColor = isEditing ? '#fff' : (item.color || '#FFFFFF');
    const itemTextColor = getTextColorForBackground(itemBackgroundColor);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        userSelect: 'none',
        padding: '10px',
        margin: '0 0 8px 0',
        backgroundColor: itemBackgroundColor,
        color: itemTextColor,
        border: isEditing ? '1px solid #007bff' : `1px solid ${item.color || '#eee'}`,
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '10px',
    };

    return (
        <div ref={setNodeRef} style={style}>
            {isEditing ? (
                <>
                    <div style={{ flex: 1, display: 'flex', gap: '14px', alignItems: 'flex-end', minWidth: 0 }}>
                        <div style={{ width: '85px', flexShrink: 0 }}>
                            <label style={{ display: 'block', fontSize: '0.65em', fontWeight: 'bold', marginBottom: '2px', whiteSpace: 'nowrap' }}>ICD CODE</label>
                            <input
                                type="text"
                                value={editedCode}
                                onChange={(e) => setEditedCode(e.target.value)}
                                className="search-input"
                                placeholder="Code"
                                style={{ width: '100%', minWidth: 0, padding: '6px', height: '34px', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div style={{ width: '26%', minWidth: '140px', flexShrink: 1 }}>
                            <label style={{ display: 'block', fontSize: '0.65em', fontWeight: 'bold', marginBottom: '2px', whiteSpace: 'nowrap' }}>ICD NAME</label>
                            <input
                                type="text"
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                className="search-input"
                                placeholder="ICD Name"
                                style={{
                                    width: '100%',
                                    minWidth: 0,
                                    padding: '6px',
                                    height: '34px',
                                    boxSizing: 'border-box',
                                    backgroundColor: editedColor,
                                    color: getTextColorForBackground(editedColor)
                                }}
                            />
                        </div>
                        <div style={{ width: '100px', flexShrink: 0 }}>
                            <label style={{ display: 'block', fontSize: '0.65em', fontWeight: 'bold', marginBottom: '2px', whiteSpace: 'nowrap' }}>SHORT NAME</label>
                            <input
                                type="text"
                                value={editedShortName}
                                onChange={(e) => setEditedShortName(e.target.value)}
                                className="search-input"
                                placeholder="Short"
                                style={{ width: '100%', minWidth: 0, padding: '6px', height: '34px', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div style={{ flexShrink: 0 }}>
                            <label style={{ display: 'block', fontSize: '0.65em', fontWeight: 'bold', marginBottom: '2px', whiteSpace: 'nowrap' }}>COLOUR</label>
                            <input
                                type="color"
                                value={editedColor}
                                onChange={(e) => setEditedColor(e.target.value)}
                                title="Select ICD Color"
                                style={{ width: '40px', height: '34px', boxSizing: 'border-box', padding: '2px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                            />
                        </div>
                        <div style={{ width: '45px', flexShrink: 0 }}>
                            <label style={{ display: 'block', fontSize: '0.65em', fontWeight: 'bold', marginBottom: '2px', whiteSpace: 'nowrap' }}>FREE DAYS</label>
                            <input
                                type="number"
                                min="0"
                                max="99"
                                value={editedFreeDays}
                                onChange={(e) => setEditedFreeDays(e.target.value)}
                                className="search-input"
                                placeholder="0"
                                style={{ width: '100%', minWidth: 0, padding: '6px 2px', textAlign: 'center', height: '34px', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div style={{ textAlign: 'center', flexShrink: 0, paddingLeft: '14px', marginLeft: '4px', borderLeft: '1px solid #ddd' }}>
                            <label style={{ display: 'block', fontSize: '0.65em', fontWeight: 'bold', marginBottom: '2px', whiteSpace: 'nowrap' }}>TERMINAL</label>
                            <div style={{ height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={editedTerminal}
                                    onChange={(e) => setEditedTerminal(e.target.checked)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="action-buttons">
                        {/* 3. Protect Save button */}
                        {hasPermission('ICDS', 'can_edit') && (
                            <button className="icon-btn save-btn" title="Save" onClick={handleSave}><i className="fa fa-save" /></button>
                        )}
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={onCancel}><i className="fa fa-times" /></button>
                    </div>
                </>
            ) : (
                <>
                    <span {...attributes} {...listeners} style={{ flex: 1, cursor: 'grab', display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <strong style={{minWidth: '150px'}}>{item.name}</strong>
                        {item.short_name && <span style={{opacity: 0.8}}>/ {item.short_name}</span>}
                        {item.free_days != null && <span style={{opacity: 0.7, fontSize: '0.85em'}}>{item.free_days} FD</span>}
                        {item.terminal && <span style={{opacity: 0.7, fontSize: '0.85em'}}>[Terminal]</span>}
                    </span>
                    <div className="action-buttons">
                        {/* 4. Protect Edit and Delete buttons */}
                        {hasPermission('ICDS', 'can_edit') && (
                            <button className="icon-btn" title="Edit" onClick={() => onEdit(id)} style={{color: itemTextColor}}><i className="fa fa-pencil" /></button>
                        )}
                        {hasPermission('ICDS', 'can_delete') && (
                            <button className="icon-btn cancel-btn" title="Delete" onClick={() => onDelete(id)}><i className="fa fa-trash" /></button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}


export default function ICDConfiguration() {
    const [icds, setIcds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newIcdName, setNewIcdName] = useState('');
    const [newIcdShortName, setNewIcdShortName] = useState('');
    const [newIcdCode, setNewIcdCode] = useState('');
    const [newIcdColor, setNewIcdColor] = useState('#FFFFFF');
    const [newIcdFreeDays, setNewIcdFreeDays] = useState('');
    const [newIcdTerminal, setNewIcdTerminal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingIcdId, setEditingIcdId] = useState(null);
    // --- FIX: Wrap hasPermission to allow alternative port config menu permission to act as ICDS ---
    const { hasPermission: rawHasPermission } = usePermissions();
    const hasPermission = (module, action) => {
        if (module === 'ICDS') {
            return rawHasPermission('ICDS', action) || rawHasPermission('PORT CONFIGURATIONS', action);
        }
        return rawHasPermission(module, action);
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchIcds = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(API_URL, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                const sortedData = (data.results || data).sort((a, b) => a.sort_order - b.sort_order);
                setIcds(sortedData);
            } else {
                toast.error('Failed to fetch ICDs.');
            }
        } catch (e) {
            toast.error('An error occurred while fetching ICDs.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchIcds();
    }, [fetchIcds]);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setIcds((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleSaveOrder = async () => {
        setSaving(true);
        try {
            const ordered_ids = icds.map(icd => icd.id);
            const res = await fetch(`${API_URL}update_order/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ ordered_ids }),
            });
            if (res.ok) {
                toast.success('ICD order saved successfully!');
                fetchIcds();
            } else {
                toast.error('Failed to save ICD order.');
            }
        } catch (e) {
            toast.error('An error occurred while saving the order.');
        } finally {
            setSaving(false);
        }
    };

    const handleAddIcd = async (e) => {
        e.preventDefault();
        if (!newIcdName.trim()) {
            toast.warn('ICD name cannot be empty.');
            return;
        }
        const newIcd = {
            name: newIcdName.trim().toUpperCase(),
            short_name: newIcdShortName.trim().toUpperCase(),
            code: newIcdCode.trim().toUpperCase(),
            color: newIcdColor,
            free_days: newIcdFreeDays === '' ? null : parseInt(newIcdFreeDays, 10),
            terminal: newIcdTerminal,
            sort_order: icds.length,
        };
        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(newIcd),
            });
            if (res.ok) {
                toast.success(`ICD "${newIcd.name}" added.`);
                setNewIcdName('');
                setNewIcdShortName('');
                setNewIcdCode('');
                setNewIcdColor('#FFFFFF');
                setNewIcdFreeDays('');
                setNewIcdTerminal(false);
                fetchIcds();
            } else {
                const errorData = await res.json();
                toast.error(errorData.name?.[0] || errorData.code?.[0] || 'Failed to add ICD.');
            }
        } catch (e) {
            toast.error('An error occurred while adding the ICD.');
        }
    };

    const handleDeleteIcd = async (icdId) => {
        if (window.confirm('Are you sure you want to delete this ICD? This action cannot be undone.')) {
            try {
                const res = await fetch(`${API_URL}${icdId}/`, {
                    method: 'DELETE',
                    headers: getAuthHeaders(),
                });
                if (res.status === 204) {
                    toast.success('ICD deleted.');
                    fetchIcds();
                } else {
                    toast.error('Failed to delete ICD.');
                }
            } catch (e) {
                toast.error('An error occurred while deleting the ICD.');
            }
        }
    };

    const handleSaveEdit = async (icdId, updatedData) => {
        try {
            const res = await fetch(`${API_URL}${icdId}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify(updatedData),
            });
            if (res.ok) {
                toast.success('ICD updated successfully!');
                setEditingIcdId(null);
                fetchIcds();
            } else {
                const errorData = await res.json();
                toast.error(errorData.name?.[0] || 'Failed to update ICD.');
            }
        } catch (e) {
            toast.error('An error occurred while updating the ICD.');
        }
    };

    const filteredIcds = searchTerm.trim()
        ? icds.filter(icd => {
            const q = searchTerm.trim().toLowerCase();
            return (icd.name || '').toLowerCase().includes(q)
                || (icd.short_name || '').toLowerCase().includes(q)
                || (icd.code || '').toLowerCase().includes(q);
        })
        : icds;

    if (loading) {
        return <div>Loading ICDs...</div>;
    }

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: 'auto' }}>
            <h2>ICD Configuration</h2>
            <p>Drag and drop the ICDs to reorder them for the Port Report. Click "Save Order" to apply changes.</p>

            <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search ICD by name, short name or code..."
                className="search-input"
                style={{ width: '100%', marginBottom: '12px' }}
            />

            {/* 6. Protect Add form */}
            {hasPermission('ICDS', 'can_create') && (
                <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
                    <h4>Add New ICD</h4>
                    <form onSubmit={handleAddIcd} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                            type="text"
                            value={newIcdCode}
                            onChange={(e) => setNewIcdCode(e.target.value)}
                            placeholder="ICD Code"
                            className="search-input"
                            style={{ width: '15%', minWidth: '80px' }}
                        />
                        <input
                            type="text"
                            value={newIcdName}
                            onChange={(e) => setNewIcdName(e.target.value)}
                            placeholder="Enter new ICD name"
                            className="search-input"
                            style={{ 
                                flex: 1,
                                backgroundColor: newIcdColor,
                                color: getTextColorForBackground(newIcdColor)
                            }}
                        />
                        <input
                            type="text"
                            value={newIcdShortName}
                            onChange={(e) => setNewIcdShortName(e.target.value)}
                            placeholder="Short Name (Optional)"
                            className="search-input"
                            style={{ width: '15%', minWidth: '80px' }}
                        />
                        <input
                            type="color"
                            value={newIcdColor}
                            onChange={(e) => setNewIcdColor(e.target.value)}
                            title="Select ICD Color"
                            style={{ width: '60px', height: '38px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                        />
                        <input
                            type="number"
                            min="0"
                            value={newIcdFreeDays}
                            onChange={(e) => setNewIcdFreeDays(e.target.value)}
                            placeholder="Free Days"
                            title="Free Days"
                            className="search-input"
                            style={{ width: '12%', minWidth: '70px' }}
                        />
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', cursor: 'pointer' }} title="Terminal">
                            <input
                                type="checkbox"
                                checked={newIcdTerminal}
                                onChange={(e) => setNewIcdTerminal(e.target.checked)}
                            />
                            Terminal
                        </label>
                        <button type="submit" className="add-btn">Add ICD</button>
                    </form>
                </div>
            )}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={filteredIcds}
                    strategy={verticalListSortingStrategy}
                >
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '10px' }}>
                        {filteredIcds.length === 0 && (
                            <div style={{ padding: '10px', color: '#666' }}>No ICDs match your search.</div>
                        )}
                        {filteredIcds.map(icd => (
                            <SortableIcdItem
                                key={icd.id}
                                id={icd.id}
                                item={icd}
                                onDelete={handleDeleteIcd}
                                isEditing={icd.id === editingIcdId}
                                onEdit={setEditingIcdId}
                                onCancel={() => setEditingIcdId(null)}
                                onSave={handleSaveEdit}
                                hasPermission={hasPermission} // 7. Pass hasPermission down
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>

            {/* 8. Protect Save Order button */}
            {hasPermission('ICDS', 'can_edit') && (
                <button onClick={handleSaveOrder} disabled={saving || editingIcdId !== null} className="add-btn" style={{ marginTop: '20px', width: '100%', padding: '12px' }}>
                    {saving ? 'Saving...' : 'Save Order'}
                </button>
            )}
        </div>
    );
}
